import fp from 'lodash/fp'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import { sanitizeMailDeliveryInput } from 'todistukset-common/src/vtj/form-sanitization.util'
import type {
  ContactInfo,
  MailDelivery,
} from 'todistukset-common/src/vtj/types/todistus'
import {
  getPersonFromUser,
  isEidasIdentifiedUser,
} from 'todistukset-common/src/vtj/user.utils'
import type { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import {
  EidasIdentifiedUser,
  HetuIdentifiedUser,
  TodistuksetUser,
} from 'todistukset-common/src/vtj/types/user'
import {
  EditableField,
  editIlmoitettuArvo,
  finalEditableFieldValue,
  Puhelinnumero,
  Sahkopostiosoite,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import {
  TodistusAsiointikieli,
  UnpersistedEidasTodistusPerson,
  UnpersistedHetuTodistusPerson,
  UnpersistedTodistusPerson,
} from 'todistukset-common/src/vtj/types/todistus-person.type'

export function getDefaultPersons(p: {
  user: HetuIdentifiedUser
  todistusType: CertificateTypes
  asiointikieli: TodistusAsiointikieli
}): UnpersistedHetuTodistusPerson[]
export function getDefaultPersons(p: {
  user: EidasIdentifiedUser
  todistusType: CertificateTypes
  asiointikieli: TodistusAsiointikieli
}): UnpersistedEidasTodistusPerson[]
export function getDefaultPersons({
  user,
  todistusType,
  asiointikieli,
}: {
  user: TodistuksetUser
  todistusType: CertificateTypes
  asiointikieli: TodistusAsiointikieli
}): UnpersistedTodistusPerson[] {
  const isOnlyOwnTodistusAvailable = fp.some(fp.identity, [
    isEidasIdentifiedUser(user),
    todistusType === CertificateTypes.AVIOLIITTOTODISTUS,
    todistusType === CertificateTypes.SIVIILISAATYTODISTUS,
    fp.isEmpty(user.huollettavat),
  ])

  if (isOnlyOwnTodistusAvailable) {
    return [getPersonFromUser(user, { isSubject: true, asiointikieli })]
  }

  return [getPersonFromUser(user, { asiointikieli })]
}

export const populateAndSanitizeMailDelivery = ({
  user = {},
  customValues = {},
}: {
  user?: Partial<TodistuksetUser>
  customValues?: Partial<MailDelivery>
}): MailDelivery => {
  const sanitizedValue = (
    userValue: string | null | undefined,
    customValue: EditableField<string> | null | undefined
  ) => {
    const ilmoitettuArvo = finalEditableFieldValue(customValue ?? null)
    // Note, the original code worked like this. Ilmotettu arvo "customValue" is dropped if we have a VTJ value from user object.
    return {
      vtjArvo: userValue ? sanitizeMailDeliveryInput(userValue) : null,
      ilmoitettuArvo:
        fp.isNil(userValue) && ilmoitettuArvo
          ? sanitizeMailDeliveryInput(ilmoitettuArvo)
          : null,
      virkailijaArvo: null,
    }
  }

  return {
    firstName: sanitizedValue(user.firstNames, customValues.firstName),
    lastName: sanitizedValue(user.lastName, customValues.lastName),
    streetAddress: sanitizedValue(
      user.streetAddress,
      customValues.streetAddress
    ),
    streetAddressExtras: sanitizedValue(
      undefined,
      customValues.streetAddressExtras
    ),
    postOffice: sanitizedValue(user.postOffice, customValues.postOffice),
    postalCode: sanitizedValue(user.postalCode, customValues.postalCode),
    country: sanitizedValue(undefined, customValues.country),
  }
}

export const clearMailDeliveryAddress = (): MailDelivery =>
  populateAndSanitizeMailDelivery({})

export const clearContactInfo = (): ContactInfo => ({
  email: editIlmoitettuArvo<Sahkopostiosoite>(null, ''),
  phoneNumber: editIlmoitettuArvo<Puhelinnumero>(null, ''),
})

export const shouldShowTranslationFormWithPreviousAddressesWarning = ({
  certificateType,
  form,
}: {
  certificateType: CertificateTypes
  form: TodistusStoreForm
}): boolean =>
  certificateType === CertificateTypes.ASUINPAIKKATODISTUS &&
  form.usageRegion.countryKoodistoCode !== EuCountries.FINLAND &&
  !!finalEditableFieldValue(form.includePreviousPermanentAddresses) &&
  !!finalEditableFieldValue(form.includeTranslationForm) &&
  finalEditableFieldValue(form.translationFormLanguage) !== null
